// Copyright (c) Docugami, Inc. All rights reserved.

import { IMessage, MessageId } from "./MessageTypes";
import { v4 as uuidv4 } from 'uuid';

export type TypedHandlerCallback<T extends IMessage> = (message: T) => void
type HandlerCallback = TypedHandlerCallback<IMessage>

/**
 * Singleton class used for sending messages and commands between components.
 * Message - 1:many registration. Messages are transient so your handler must exist at the point they are fired to receive them
 * Command - 1:1 registration. Commands will persist until a handler process them
 */
class MessageBus {
    private readonly _messageHandlers = new Map<MessageId, string[]>();
    private readonly _commandHandlers = new Map<MessageId, HandlerCallback>();
    private readonly _callbackLookup = new Map<string, HandlerCallback>();
    private _pendingCommands: IMessage[] = [];

    public registerMessageHandler<T extends IMessage>(id: MessageId, callback: TypedHandlerCallback<T>): () => void {
        const callbackWrapper = (message: IMessage) => {
            callback(message as T);
        }

        let callbackIds = this._messageHandlers.get(id);
        if (!callbackIds) {
            callbackIds = [];
            this._messageHandlers.set(id, callbackIds);
        }

        const callbackId = uuidv4();
        this._callbackLookup.set(callbackId, callbackWrapper);
        callbackIds.push(callbackId);

        return () => {
            this._callbackLookup.delete(callbackId);
        }
    }

    public registerCommandHandler<T extends IMessage>(id: MessageId, callback: TypedHandlerCallback<T>): () => void {
        if (this._commandHandlers.has(id)) {
            throw new Error("Cannot duplicate command handler registrations");
        }

        const callbackWrapper = (message: IMessage) => {
            callback(message as T);
        }

        this._commandHandlers.set(id, callbackWrapper);

        // Commands support delayed handling so we will flush our commands when we get new handlers
        this.flushCommands();

        return () => {
            this._commandHandlers.delete(id);
        }
    }

    public sendMessage(message: IMessage) {
        const id = message.id;
        const callbackIds = this._messageHandlers.get(id);
        if (!callbackIds) {
            return;
        }

        for (let i = callbackIds.length - 1; i >= 0; --i) {
            const callbackId = callbackIds[i];
            const callback = this._callbackLookup.get(callbackId);
            if (!callback) {
                callbackIds.splice(i, 1);
            }
            else {
                callback(message);
            }
        }
    }

    public sendCommand(message: IMessage, required: boolean = false): boolean {
        const callback = this._commandHandlers.get(message.id);
        if (!callback) {
            if (!required) {
                this._pendingCommands = [message, ...this._pendingCommands];
            }

            return false;
        }

        callback(message);
        return true;
    }

    private flushCommands() {
        for (let i = this._pendingCommands.length - 1; i >= 0; --i) {
            const command = this._pendingCommands[i];
            const succeeded = this.sendCommand(command);
            if (succeeded) {
                this._pendingCommands.splice(i, 1);
            }
        }
    }
}

export const MessageBusSingleton = new MessageBus();
